import React from 'react';

import PrestaGlobals from '../common/PrestaGlobals';
import IParamScript from '../database/interfaces/paramScript/IParamScript';

/**
 * Holds Configurator Admin-mode UI state.
 *
 * `isAdmin` mirrors the server-provided flag (a logged-in back-office employee) and only
 * controls whether admin UI is rendered. It is NOT an authorization boundary — every admin
 * write goes through the employee-gated `ucadmin` controller, which re-verifies server-side.
 */
export const AdminContext = React.createContext<{
    isAdmin: boolean;
    panelOpen: boolean;
    setPanelOpen: (open: boolean) => void;
    /** The parameter currently open in the descriptor editor dialog, if any. */
    editingParam: IParamScript | null;
    openDescriptorEditor: (param: IParamScript) => void;
    closeDescriptorEditor: () => void;
}>({
    isAdmin: false,
    panelOpen: false,
    setPanelOpen: () => {},
    editingParam: null,
    openDescriptorEditor: () => {},
    closeDescriptorEditor: () => {},
});

/**
 * Provides admin UI state. Mounted only when `PrestaGlobals.isAdmin` is true, so the customer
 * bundle path is unaffected.
 */
const AdminProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
    const [panelOpen, setPanelOpen] = React.useState(true); // open on /designeradmin load
    const [editingParam, setEditingParam] = React.useState<IParamScript | null>(null);

    const value = React.useMemo(() => ({
        isAdmin: PrestaGlobals.isAdmin,
        panelOpen,
        setPanelOpen,
        editingParam,
        openDescriptorEditor: (param: IParamScript) => setEditingParam(param),
        closeDescriptorEditor: () => setEditingParam(null),
    }), [panelOpen, editingParam]);

    return (
        <AdminContext.Provider value={value}>
            {children}
        </AdminContext.Provider>
    );
};

export default AdminProvider;
